import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2, ImagePlus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const MEALS = [["breakfast", "Breakfast"], ["lunch", "Lunch"], ["dinner", "Dinner"], ["snack", "Snack"]];
const EMPTY = { name: "", meal: "", tags: "", kcal: "", protein: "", carb: "", fat: "", ingredients: "", method: "" };

export default function RecipeSubmitDialog({ open, onOpenChange, onSubmitted }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoPath, setPhotoPath] = useState(null);
  const [preview, setPreview] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const reset = () => { setF(EMPTY); setPhotoPath(null); setPreview(null); };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8MB)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/community-recipes/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(data.photo_path);
      setPreview(URL.createObjectURL(file));
    } catch (err) {
      toast.error("Photo upload failed — please try a smaller image.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!f.name.trim() || !f.meal || !f.ingredients.trim() || !f.method.trim()) {
      toast.error("Please add a name, meal type, ingredients and a method.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/community-recipes", {
        name: f.name, meal: f.meal,
        tags: f.tags, kcal: f.kcal, protein: f.protein, carb: f.carb, fat: f.fat,
        ingredients: f.ingredients, method: f.method, photo_path: photoPath,
      });
      toast.success("Thanks! Your recipe has been sent for review.");
      reset();
      onOpenChange(false);
      onSubmitted && onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const lbl = "text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-1 block";
  const inp = "bg-black border-white/15 text-white rounded-none";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="force-dark bg-[#141414] border-white/15 text-white max-w-lg max-h-[90vh] overflow-y-auto rounded-none" data-testid="recipe-submit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-white">Share a recipe</DialogTitle>
          <DialogDescription className="text-zinc-300 text-sm">
            Send us a meal or snack you love. If we add it, it appears as a <span className="text-[#E2E30A] font-semibold">User contribution</span> — your name is never shown.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5 py-1">
          <div>
            <label className={lbl}>Recipe name *</label>
            <Input data-testid="recipe-form-name" className={inp} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Overnight oats with berries" />
          </div>

          <div>
            <label className={lbl}>Photo <span className="text-zinc-300 normal-case tracking-normal">(optional)</span></label>
            {preview ? (
              <div className="relative w-full">
                <img src={preview} alt="Preview" className="w-full max-h-56 object-cover border border-white/15" />
                <button type="button" data-testid="recipe-form-remove-photo" onClick={() => { setPhotoPath(null); setPreview(null); }}
                  className="absolute top-2 right-2 h-7 w-7 grid place-items-center bg-black/70 border border-white/20 text-white hover:text-[#FF3B30]"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <label data-testid="recipe-form-photo-label" className="flex items-center justify-center gap-2 w-full py-4 border border-dashed border-white/20 text-zinc-300 hover:border-[#E2E30A]/60 hover:text-white cursor-pointer transition-colors">
                {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : <><ImagePlus className="h-4 w-4" /> Add a photo</>}
                <input data-testid="recipe-form-photo" type="file" accept="image/*" className="hidden" onChange={onPhoto} disabled={uploading} />
              </label>
            )}
          </div>

          <div>
            <label className={lbl}>Meal type *</label>
            <Select value={f.meal} onValueChange={(v) => set("meal", v)}>
              <SelectTrigger data-testid="recipe-form-meal" className={inp}><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/15 text-white rounded-none">
                {MEALS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={lbl}>Dietary tags <span className="text-zinc-300 normal-case tracking-normal">(comma separated, optional)</span></label>
            <Input data-testid="recipe-form-tags" className={inp} value={f.tags} onChange={(e) => set("tags", e.target.value)} placeholder="Vegan, High protein, Gluten-free" />
          </div>
          <div>
            <label className={lbl}>Macros per serving <span className="text-zinc-300 normal-case tracking-normal">(optional)</span></label>
            <div className="grid grid-cols-4 gap-2">
              <Input data-testid="recipe-form-kcal" className={inp} type="number" value={f.kcal} onChange={(e) => set("kcal", e.target.value)} placeholder="kcal" />
              <Input data-testid="recipe-form-protein" className={inp} type="number" value={f.protein} onChange={(e) => set("protein", e.target.value)} placeholder="P (g)" />
              <Input data-testid="recipe-form-carb" className={inp} type="number" value={f.carb} onChange={(e) => set("carb", e.target.value)} placeholder="C (g)" />
              <Input data-testid="recipe-form-fat" className={inp} type="number" value={f.fat} onChange={(e) => set("fat", e.target.value)} placeholder="F (g)" />
            </div>
          </div>
          <div>
            <label className={lbl}>Ingredients * <span className="text-zinc-300 normal-case tracking-normal">(one per line)</span></label>
            <Textarea data-testid="recipe-form-ingredients" rows={4} className={inp} value={f.ingredients} onChange={(e) => set("ingredients", e.target.value)} placeholder={"80g rolled oats\n200ml milk\n1 handful blueberries"} />
          </div>
          <div>
            <label className={lbl}>Method * <span className="text-zinc-300 normal-case tracking-normal">(one step per line)</span></label>
            <Textarea data-testid="recipe-form-method" rows={4} className={inp} value={f.method} onChange={(e) => set("method", e.target.value)} placeholder={"Mix oats and milk\nLeave overnight in the fridge\nTop with berries and enjoy"} />
          </div>
        </div>

        <DialogFooter>
          <Button data-testid="recipe-form-submit" onClick={submit} disabled={busy || uploading}
            className="w-full rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-bold uppercase tracking-wider">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending…</> : <><Send className="h-4 w-4 mr-2" /> Send for review</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
